import { useEffect, useState } from "react";
import { FileText, X, AlertTriangle, FileWarning, Shield, ShieldOff, Trash2, Plus, Check } from "lucide-react";
import AdminShell, { Panel, StatusPill, Btn } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { PrimaryTabs, FilterChips } from "@/components/admin/SubTabs";
import DataTable from "@/components/admin/DataTable";
import AdminPager from "@/components/admin/AdminPager";
import { api } from "@/lib/api";
import { asAdminList, useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const TABS = [
  { id: "monitoring", label: "Transaction Monitoring", icon: AlertTriangle },
  { id: "velocity_rules", label: "Velocity Rules", icon: Shield },
  { id: "reporting", label: "FIU Reporting", icon: FileWarning },
  { id: "blacklist", label: "Blacklists & Sanctions", icon: ShieldOff },
];

const PATTERN_FILTERS = [
  { id: "", label: "All" },
  { id: "Velocity Rule", label: "Velocity" },
  { id: "Blacklist", label: "Blacklist" },
  { id: "Structuring", label: "Structuring" },
  { id: "Layering", label: "Layering" },
  { id: "Rapid Movement", label: "Rapid Movement" },
  { id: "High Risk Jurisdiction", label: "High Risk Jurisdiction" },
];
const BLACKLIST_KINDS = [
  { id: "wallet_address", label: "Wallet address" },
  { id: "email", label: "Email" },
  { id: "user_id", label: "User ID" },
];
const ACTION_LABEL = {
  freeze_trading: "Freeze trading",
  freeze_withdrawals: "Freeze withdrawals",
  enhanced_kyc: "Enhanced KYC",
  alert_only: "Alert only",
};
const EMPTY_RULE = {
  rule_id: "", name: "", metric: "", threshold: 0, time_window: 3600,
  action: "alert_only", enabled: true, event_type: "trade", measure: "count",
};
const fmt = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }));
const num = (v) => Number(v ?? 0).toLocaleString();
const isObjectId = (id) => /^[a-f0-9]{24}$/i.test(String(id || ""));

export default function FiuAml() {
  const [tab, setTab] = useState("monitoring");
  const [alerts, setAlerts] = useState([]);
  const [velocity, setVelocity] = useState([]);
  const [strs, setStrs] = useState([]);
  const [velocityRules, setVelocityRules] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [summary, setSummary] = useState({ pending_reviews: 0, escalated: 0, strs_mtd: 0, velocity_alerts: 0, velocity_critical: 0 });
  const [filing, setFiling] = useState(null);
  const [patternFilter, setPatternFilter] = useState("");
  const [blForm, setBlForm] = useState({ kind: "wallet_address", value: "", reason: "" });
  const [blBusy, setBlBusy] = useState(false);
  const alertsPager = useAdminPager();
  const velocityPager = useAdminPager();
  const strPager = useAdminPager();
  
  const [isCreatingRule, setIsCreatingRule] = useState(false);
  const [ruleForm, setRuleForm] = useState(EMPTY_RULE);

  async function load() {
    const [a, v, s, rules, bl, sum] = await Promise.all([
      api.get("/api/admin/aml/alerts", {
        params: { skip: alertsPager.skip, limit: alertsPager.limit, ...(patternFilter ? { type: patternFilter } : {}) },
      }),
      api.get("/api/admin/aml/velocity-alerts", { params: { skip: velocityPager.skip, limit: velocityPager.limit } }),
      api.get("/api/admin/aml/str-reports", { params: { skip: strPager.skip, limit: strPager.limit } }),
      api.get("/api/admin/safety/velocity-rules"),
      api.get("/api/admin/aml/blacklist"),
      api.get("/api/admin/aml/summary"),
    ]);
    const alerts = unwrapAdminList(a.data, "alerts");
    setAlerts(alerts.items);
    alertsPager.setTotal(alerts.total);
    const vel = unwrapAdminList(v.data);
    setVelocity(vel.items);
    velocityPager.setTotal(vel.total);
    const strs = unwrapAdminList(s.data);
    setStrs(strs.items);
    strPager.setTotal(strs.total);
    setVelocityRules(Array.isArray(rules.data) ? rules.data : []);
    setBlacklist(unwrapAdminList(bl.data).items);
    setSummary(sum.data || {});
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [alertsPager.page, velocityPager.page, strPager.page, patternFilter]);

  async function clearAlert(id) {
    await api.post(`/api/admin/aml/alerts/${id}/clear`);
    load();
  }

  async function handleUnfreeze(userId) {
    try {
      await api.post("/api/admin/safety/unfreeze-user", { user_id: userId });
      alert("Account hold released.");
      load();
    } catch (e) {
      alert("Failed to unfreeze user: " + (e.response?.data?.detail || e.message));
    }
  }

  async function handleSaveRule(e) {
    e.preventDefault();
    try {
      await api.post("/api/admin/safety/velocity-rules", {
        rule_id: ruleForm.rule_id,
        name: ruleForm.name,
        metric: ruleForm.metric,
        threshold: Number(ruleForm.threshold),
        time_window: Number(ruleForm.time_window),
        action: ruleForm.action,
        enabled: !!ruleForm.enabled,
        event_types: [ruleForm.event_type || "trade"],
        measure: ruleForm.measure || "count",
      });
      setIsCreatingRule(false);
      setEditingRule(null);
      load();
    } catch (e) {
      alert("Failed to save rule: " + (e.response?.data?.detail || e.message));
    }
  }

  async function handleDeleteRule(ruleId) {
    if (!confirm("Are you sure you want to delete this rule?")) return;
    try {
      await api.delete(`/api/admin/safety/velocity-rules/${ruleId}`);
      load();
    } catch (e) {
      alert("Failed to delete rule");
    }
  }

  function openRuleEditor(rule) {
    if (!rule) {
      setRuleForm(EMPTY_RULE);
    } else {
      setRuleForm({
        rule_id: rule.rule_id || "",
        name: rule.name || "",
        metric: rule.metric || "",
        threshold: rule.threshold ?? 0,
        time_window: rule.time_window ?? 3600,
        action: rule.action || "alert_only",
        enabled: rule.enabled !== false,
        event_type: (rule.event_types && rule.event_types[0]) || "trade",
        measure: rule.measure || (Number(rule.time_window) === 0 ? "single" : "count"),
      });
    }
    setIsCreatingRule(true);
  }

  async function handleSaveBlacklist(e) {
    e.preventDefault();
    setBlBusy(true);
    try {
      await api.post("/api/admin/aml/blacklist", blForm);
      setBlForm({ kind: "wallet_address", value: "", reason: "" });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to add blacklist entry");
    } finally {
      setBlBusy(false);
    }
  }

  async function handleRemoveBlacklist(entryId) {
    if (!confirm("Remove this blacklist entry?")) return;
    try {
      await api.delete(`/api/admin/aml/blacklist/${entryId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to remove entry");
    }
  }

  const alertRows = asAdminList(alerts);

  return (
    <AdminShell title="FIU / AML" subtitle="Suspicious transaction reports and AML screening.">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Pending Reviews" value={summary.pending_reviews ?? 0} icon={AlertTriangle} variant="yellow" />
        <KpiCard label="Escalated" value={summary.escalated ?? 0} icon={Shield} variant="purple" />
        <KpiCard label="Filed STRs (MTD)" value={summary.strs_mtd ?? 0} icon={FileText} variant="red" />
        <KpiCard label="Velocity Alerts" value={summary.velocity_alerts ?? 0} delta={`${summary.velocity_critical ?? 0} critical`} icon={AlertTriangle} variant="red" />
      </div>

      <PrimaryTabs tabs={TABS} value={tab} onChange={setTab} testIdPrefix="aml-tab" />
      
      <div className="mt-5">
        {tab === "monitoring" && (
          <div className="space-y-5">
            <Panel>
              <div className="p-4 border-b border-line flex justify-between items-center">
                <h3 className="text-[14px] font-semibold">Suspicious Pattern Detection</h3>
                <FilterChips options={PATTERN_FILTERS} value={patternFilter} onChange={(id) => { setPatternFilter(id); alertsPager.resetPage(); }} testIdPrefix="pattern" />
              </div>
              <DataTable
                testid="aml-alerts-table"
                columns={[
                  { key: "user", header: "User", render: (a) => <div><div className="font-semibold">{a.user_name}</div><div className="text-[11px] text-muted-foreground">{a.user_email}</div></div> },
                  { key: "type", header: "Pattern", render: (a) => <div><div className="font-semibold">{a.type}</div><div className="text-[11px] text-muted-foreground max-w-[280px] truncate">{a.pattern}</div></div> },
                  { key: "amount", header: "Amount", align: "right", render: (a) => <span className="tabular-nums">{fmt(a.amount_inr)}</span> },
                  { key: "score", header: "Risk", align: "right", render: (a) => <span className={`font-semibold ${a.risk_score >= 90 ? "text-down" : a.risk_score >= 70 ? "text-warning" : "text-up"}`}>{a.risk_score}</span> },
                  { key: "status", header: "Status", render: (a) => <StatusPill value={a.status} /> },
                  { key: "actions", header: "Actions", align: "right", render: (a) => (
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setFiling(a)} className="text-down text-[11px] hover:underline" data-testid={`file-str-${a.alert_id}`}>File STR</button>
                      <button onClick={() => clearAlert(a.id)} className="text-muted-foreground text-[11px] hover:underline">Clear</button>
                    </div>
                  )},
                ]}
                rows={alertRows}
                rowKey={(a) => a.id || a.alert_id}
                empty="No live AML alerts. Breaches from velocity rules and the blacklist appear here."
              />
              <AdminPager page={alertsPager.page} total={alertsPager.total} pageSize={alertsPager.pageSize} onPage={alertsPager.setPage} testId="aml-alerts-pager" />
            </Panel>

            <Panel>
              <div className="p-4 border-b border-line"><h3 className="text-[14px] font-semibold">Velocity Check Alerts</h3></div>
              <DataTable
                columns={[
                  { key: "user", header: "User", render: (v) => <div><div className="font-semibold">{v.user_name}</div><div className="text-[11px] text-muted-foreground">{v.user_email}</div></div> },
                  { key: "metric", header: "Metric" },
                  { key: "current", header: "Current", align: "right", render: (v) => <span className="font-semibold tabular-nums">{num(v.current)}</span> },
                  { key: "threshold", header: "Threshold", align: "right", render: (v) => <span className="text-muted-foreground tabular-nums">{num(v.threshold)}</span> },
                  { key: "risk", header: "Risk", render: (v) => <StatusPill value={v.risk_level} /> },
                  { key: "status", header: "Status", render: (v) => <StatusPill value={v.status} /> },
                  { key: "freeze_status", header: "Compliance Action", align: "right", render: (v) => (
                    v.risk_level === "critical" && isObjectId(v.user_id) ? (
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-[10px] text-down bg-down/10 px-2 py-0.5 rounded-[4px] font-semibold border border-down/20">HOLD APPLIED</span>
                        <button onClick={() => handleUnfreeze(v.user_id)} className="text-primary text-[11px] hover:underline font-semibold">Release hold</button>
                      </div>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">{v.risk_level === "critical" ? "Hold applied" : "Monitor only"}</span>
                    )
                  )}
                ]}
                rows={velocity}
                rowKey={(v) => v.alert_id}
                empty="No velocity breaches yet. Enabled rules raise alerts from live trades, deposits, and withdrawals."
              />
              <AdminPager page={velocityPager.page} total={velocityPager.total} pageSize={velocityPager.pageSize} onPage={velocityPager.setPage} testId="aml-velocity-pager" />
            </Panel>
          </div>
        )}

        {tab === "velocity_rules" && (
          <div className="space-y-5">
            <div className="flex justify-between items-center mb-1">
              <h2 className="text-[15px] font-semibold">Configure Transaction Velocity Limits</h2>
              <Btn onClick={() => openRuleEditor(null)} className="h-9"><Plus size={14} /> Create New Rule</Btn>
            </div>
            
            <Panel>
              <DataTable
                columns={[
                  { key: "name", header: "Rule Name", render: (r) => <span className="font-semibold">{r.name}</span> },
                  { key: "metric", header: "Metric" },
                  { key: "threshold", header: "Limit Threshold", align: "right", render: (r) => <span className="font-mono">{r.threshold.toLocaleString()}</span> },
                  { key: "window", header: "Time Window", align: "right", render: (r) => <span className="text-muted-foreground">{r.time_window}s</span> },
                  { key: "action", header: "Trigger Action", render: (r) => <span className={`text-[12px] font-semibold ${r.action === "alert_only" ? "text-warning" : "text-down"}`}>{ACTION_LABEL[r.action] || r.action}</span> },
                  { key: "enabled", header: "Status", render: (r) => <StatusPill value={r.enabled ? "active" : "disabled"} /> },
                  { key: "actions", header: "Actions", align: "right", render: (r) => (
                    <div className="flex justify-end gap-3">
                      <button onClick={() => openRuleEditor(r)} className="text-primary text-[12px] hover:underline">Edit</button>
                      <button onClick={() => handleDeleteRule(r.rule_id)} className="text-down text-[12px] hover:underline"><Trash2 size={13} /></button>
                    </div>
                  )}
                ]}
                rows={velocityRules}
                rowKey={(r) => r.rule_id}
              />
            </Panel>
          </div>
        )}

        {tab === "reporting" && (
          <Panel>
            <div className="p-4 border-b border-line"><h3 className="text-[14px] font-semibold">STR Reports Filed with FIU-IND</h3></div>
            <DataTable
              columns={[
                { key: "filed_at", header: "Filed At", render: (s) => <span className="text-muted-foreground">{new Date(s.filed_at).toLocaleString()}</span> },
                { key: "user_id", header: "User ID" },
                { key: "pattern", header: "Pattern" },
                { key: "amount", header: "Amount", align: "right", render: (s) => fmt(s.amount_inr) },
                { key: "filed_by", header: "Filed By" },
                { key: "status", header: "Status", render: (s) => <StatusPill value={s.status} /> },
              ]}
              rows={strs}
              rowKey={(s) => s.id}
              empty="No STRs filed yet."
            />
            <AdminPager page={strPager.page} total={strPager.total} pageSize={strPager.pageSize} onPage={strPager.setPage} testId="aml-str-pager" />
          </Panel>
        )}

        {tab === "blacklist" && (
          <div className="space-y-5">
            <Panel className="p-4">
              <h3 className="text-[14px] font-semibold">Add screening entry</h3>
              <p className="mt-1 text-[12px] text-muted-foreground">Active entries block matching withdrawals and open an AML alert.</p>
              <form onSubmit={handleSaveBlacklist} className="mt-4 grid gap-3 md:grid-cols-[160px_1fr_1fr_auto]">
                <select value={blForm.kind} onChange={(e) => setBlForm({ ...blForm, kind: e.target.value })} className="rounded-md border border-line bg-background p-2 text-[13px]">
                  {BLACKLIST_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                </select>
                <input required value={blForm.value} onChange={(e) => setBlForm({ ...blForm, value: e.target.value })} placeholder="Address, email, or user id" className="rounded-md border border-line bg-background p-2 text-[13px]" />
                <input value={blForm.reason} onChange={(e) => setBlForm({ ...blForm, reason: e.target.value })} placeholder="Reason" className="rounded-md border border-line bg-background p-2 text-[13px]" />
                <Btn type="submit" disabled={blBusy} className="h-9"><Plus size={14} /> Add</Btn>
              </form>
            </Panel>
            <Panel>
              <div className="border-b border-line p-4"><h3 className="text-[14px] font-semibold">Active blacklist</h3></div>
              <DataTable
                testid="aml-blacklist-table"
                columns={[
                  { key: "kind", header: "Type", render: (r) => BLACKLIST_KINDS.find((k) => k.id === r.kind)?.label || r.kind },
                  { key: "value", header: "Value", render: (r) => <span className="font-mono text-[12px]">{r.value}</span> },
                  { key: "reason", header: "Reason", render: (r) => r.reason || "—" },
                  { key: "created_by", header: "Added by" },
                  { key: "created_at", header: "Added", render: (r) => <span className="text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</span> },
                  { key: "actions", header: "", align: "right", render: (r) => (
                    <button type="button" onClick={() => handleRemoveBlacklist(r.entry_id)} className="text-down text-[12px] hover:underline">Remove</button>
                  ) },
                ]}
                rows={blacklist}
                rowKey={(r) => r.entry_id}
                empty="No active blacklist entries."
              />
            </Panel>
          </div>
        )}
      </div>

      {/* Rule Creator Modal */}
      {isCreatingRule && (
        <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-4" onClick={() => setIsCreatingRule(false)}>
          <form onSubmit={handleSaveRule} onClick={(e) => e.stopPropagation()} className="bg-surface border border-line rounded-xl w-full max-w-[500px] p-5">
            <div className="flex justify-between mb-4">
              <h3 className="text-[16px] font-semibold">{ruleForm.rule_id ? "Edit Velocity Rule" : "Create Velocity Rule"}</h3>
              <button type="button" onClick={() => setIsCreatingRule(false)} className="text-muted-foreground"><X size={16} /></button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] text-muted-foreground mb-1">Rule ID</label>
                <input 
                  type="text" 
                  disabled={!!ruleForm.rule_id}
                  value={ruleForm.rule_id} 
                  onChange={(e) => setRuleForm({ ...ruleForm, rule_id: e.target.value })} 
                  placeholder="e.g. max_deposits_1h"
                  required
                  className="w-full p-2 bg-background border border-line rounded-md text-[13px] disabled:opacity-50"
                />
              </div>

              <div>
                <label className="block text-[12px] text-muted-foreground mb-1">Rule Name</label>
                <input 
                  type="text" 
                  value={ruleForm.name} 
                  onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} 
                  placeholder="e.g. Hourly Deposit Max"
                  required
                  className="w-full p-2 bg-background border border-line rounded-md text-[13px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] text-muted-foreground mb-1">Metric Description</label>
                  <input 
                    type="text" 
                    value={ruleForm.metric} 
                    onChange={(e) => setRuleForm({ ...ruleForm, metric: e.target.value })} 
                    placeholder="e.g. Deposit/hour"
                    required
                    className="w-full p-2 bg-background border border-line rounded-md text-[13px]"
                  />
                </div>
                <div>
                  <label className="block text-[12px] text-muted-foreground mb-1">Limit Threshold (Value)</label>
                  <input 
                    type="number" 
                    value={ruleForm.threshold} 
                    onChange={(e) => setRuleForm({ ...ruleForm, threshold: parseFloat(e.target.value) })} 
                    required
                    className="w-full p-2 bg-background border border-line rounded-md text-[13px]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] text-muted-foreground mb-1">Time Window (seconds)</label>
                  <input 
                    type="number" 
                    value={ruleForm.time_window} 
                    onChange={(e) => setRuleForm({ ...ruleForm, time_window: parseInt(e.target.value) })} 
                    required
                    className="w-full p-2 bg-background border border-line rounded-md text-[13px]"
                  />
                </div>
                <div>
                  <label className="block text-[12px] text-muted-foreground mb-1">Applies to</label>
                  <select
                    value={ruleForm.event_type}
                    onChange={(e) => setRuleForm({ ...ruleForm, event_type: e.target.value })}
                    className="w-full p-2 bg-background border border-line rounded-md text-[13px]"
                  >
                    <option value="trade">Trades</option>
                    <option value="deposit">Deposits</option>
                    <option value="withdraw">Withdrawals</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] text-muted-foreground mb-1">Measure</label>
                  <select
                    value={ruleForm.measure}
                    onChange={(e) => setRuleForm({ ...ruleForm, measure: e.target.value })}
                    className="w-full p-2 bg-background border border-line rounded-md text-[13px]"
                  >
                    <option value="count">Count in window</option>
                    <option value="volume">INR volume in window</option>
                    <option value="single">Single transaction (INR)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] text-muted-foreground mb-1">Action on Breach</label>
                  <select 
                    value={ruleForm.action} 
                    onChange={(e) => setRuleForm({ ...ruleForm, action: e.target.value })}
                    className="w-full p-2 bg-background border border-line rounded-md text-[13px]"
                  >
                    <option value="alert_only">Alert compliance</option>
                    <option value="freeze_trading">Freeze trading</option>
                    <option value="freeze_withdrawals">Freeze withdrawals</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="enabled"
                  checked={ruleForm.enabled} 
                  onChange={(e) => setRuleForm({ ...ruleForm, enabled: e.target.checked })} 
                />
                <label htmlFor="enabled" className="text-[13px] text-foreground">Rule Enabled</label>
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <Btn type="button" kind="ghost" onClick={() => setIsCreatingRule(false)} className="flex-1">Cancel</Btn>
              <Btn type="submit" kind="primary" className="flex-1"><Check size={14} /> Save Rule</Btn>
            </div>
          </form>
        </div>
      )}

      {filing && <StrModal alert={filing} onClose={() => setFiling(null)} onFiled={() => { setFiling(null); load(); }} />}
    </AdminShell>
  );
}

function StrModal({ alert, onClose, onFiled }) {
  const [notes, setNotes] = useState("");
  async function file() {
    await api.post("/api/admin/aml/file-str", {
      user_id: alert.user_id,
      pattern: alert.type,
      amount_inr: alert.amount_inr,
      notes,
      alert_id: alert.alert_id || alert.id,
    });
    onFiled();
  }
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-surface border border-line rounded-xl w-full max-w-[500px] p-5" data-testid="str-modal">
        <div className="flex justify-between mb-4"><h3 className="text-[16px] font-semibold">File Suspicious Transaction Report</h3><button onClick={onClose} className="text-muted-foreground"><X size={16} /></button></div>
        <div className="text-[13px] text-muted-foreground mb-3">User: <span className="text-foreground">{alert.user_email}</span></div>
        <div className="text-[13px] text-muted-foreground mb-3">Pattern: <span className="text-foreground">{alert.type}</span> · Risk score {alert.risk_score}</div>
        <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Compliance notes for FIU..." className="w-full p-3 bg-background border border-line rounded-md text-[13px]" data-testid="str-notes" />
        <Btn kind="danger" onClick={file} className="w-full mt-4" data-testid="str-submit"><FileText size={14} /> File STR with FIU-IND</Btn>
      </div>
    </div>
  );
}
